import React from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { TrendingUp, Users2, Trophy, CheckCircle2, MessageSquareText } from "lucide-react";
import { StarRating } from "@/components/StarRating";

function ChartCard({ title, icon: Icon, children, testId, span = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className={`rounded-2xl border border-white/[0.08] bg-[#131a29]/80 backdrop-blur-xl p-6 shadow-[0_8px_32px_rgba(0,0,0,0.12)] ${span}`}
      data-testid={testId}
    >
      <p className="text-xs font-semibold text-[#8a8f98] flex items-center gap-1.5 mb-4"><Icon className="w-3.5 h-3.5 text-[#A855F7]" /> {title}</p>
      {children}
    </motion.div>
  );
}

function EmptyMetric({ label, testId }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/[0.12] p-6 flex flex-col items-center justify-center text-center h-full min-h-[140px]" data-testid={testId}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-[11px] text-slate-600 mt-1">Awaiting Data — generate more activity to unlock this metric</p>
    </div>
  );
}

const tooltipStyle = { background: "#131a29", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 12 };

export default function CreatorAnalytics({ analytics }) {
  const a = analytics || {};
  const hasRevenue = (a.revenue_chart || []).some((d) => d.amount > 0);
  const hasEnrollments = (a.daily_enrollments || []).some((d) => d.count > 0);

  return (
    <div data-testid="creator-analytics-section">
      <h2 className="text-xl sm:text-2xl font-medium text-[#f7f8f8] mb-4">Analytics</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Revenue (Last 14 Days)" icon={TrendingUp} testId="chart-revenue" span="lg:col-span-2">
          {hasRevenue ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={a.revenue_chart}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#8a8f98", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#8a8f98" }} formatter={(v) => [`₹${v}`, "Revenue"]} />
                <Area type="monotone" dataKey="amount" stroke="#3B82F6" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyMetric label="No revenue yet" testId="empty-revenue-chart" />
          )}
        </ChartCard>

        <ChartCard title="Daily Enrollments" icon={Users2} testId="chart-enrollments">
          {hasEnrollments ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={a.daily_enrollments}>
                <XAxis dataKey="date" tick={{ fill: "#8a8f98", fontSize: 10 }} tickFormatter={(d) => d.slice(8)} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "#8a8f98" }} />
                <Bar dataKey="count" fill="#A855F7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyMetric label="No enrollments yet" testId="empty-enrollments-chart" />
          )}
        </ChartCard>

        <ChartCard title="Course Performance" icon={Trophy} testId="chart-course-performance" span="lg:col-span-2">
          {(a.course_performance || []).length > 0 ? (
            <div className="space-y-2.5">
              {a.course_performance.map((c) => (
                <div key={c.course_id} className="flex items-center justify-between text-sm" data-testid={`course-performance-row-${c.course_id}`}>
                  <span className="text-slate-300 truncate flex-1">{c.title}</span>
                  <span className="text-[#8a8f98] text-xs mr-4">{c.students} students</span>
                  <span className="font-semibold text-[#f7f8f8]">₹{c.revenue.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyMetric label="No course sales yet" testId="empty-course-performance" />
          )}
        </ChartCard>

        {a.completion_rate != null ? (
          <ChartCard title="Completion Rate" icon={CheckCircle2} testId="chart-completion-rate">
            <div className="flex flex-col items-center justify-center h-full min-h-[140px]">
              <p className="text-4xl font-bold text-emerald-300" data-testid="completion-rate-value">{a.completion_rate}%</p>
              <p className="text-xs text-slate-500 mt-1.5">of enrolled learners finished every lecture</p>
            </div>
          </ChartCard>
        ) : (
          <EmptyMetric label="Completion Rate" testId="empty-completion-rate" />
        )}

        <ChartCard title="Recent Reviews" icon={MessageSquareText} testId="chart-recent-reviews" span="lg:col-span-2">
          {(a.recent_reviews || []).length > 0 ? (
            <div className="space-y-3">
              {a.recent_reviews.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-3" data-testid={`recent-review-row-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-200 truncate">{r.student_name} <span className="text-slate-500">· {r.course_title}</span></p>
                    {r.comment && <p className="text-xs text-slate-500 truncate mt-0.5">{r.comment}</p>}
                  </div>
                  <StarRating rating={r.rating} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyMetric label="No reviews yet" testId="empty-recent-reviews" />
          )}
        </ChartCard>

        <EmptyMetric label="Watch Time & Conversion Rate" testId="empty-watch-time-conversion" />
      </div>
    </div>
  );
}
